import { Matrix4, Cartesian3, Matrix3 } from "cesium";
import { EngineeringCsModel } from "components/modules/Projects/lib/types/EngineeringCsModel";

// Get Engineering CS Transform to convert from GDA2020 to Engineering CS
export function getEngineeringCsTransform(engineeringCs: EngineeringCsModel) {
  if (!engineeringCs) throw new Error("engineeringCs is required.");
  if (!engineeringCs.transformation) {
    throw new Error("engineeringCs.transformation is required.");
  }
  if (
    [0, 1, 2, 3, 4].some((i: number) =>
      [undefined, null].includes(engineeringCs.transformation[i]),
    )
  ) {
    throw new Error("engineeringCs.transformation must have all 5 parameters.");
  }

  const reversed = [...engineeringCs.transformation].reverse();

  // TRANSLATE 1
  const translationCart_1 = new Cartesian3(
    reversed[0].value[0],
    reversed[0].value[1],
    reversed[0].value[2],
  );

  const translatedMatrix1 = Matrix4.multiplyByTranslation(
    Matrix4.IDENTITY,
    translationCart_1,
    new Matrix4(),
  );

  // TRANSLATE 2
  const translationCart_2 = new Cartesian3(
    reversed[1].value[0],
    reversed[1].value[1],
    reversed[1].value[2],
  );

  const translatedMatrix2 = Matrix4.multiplyByTranslation(
    translatedMatrix1,
    translationCart_2,
    new Matrix4(),
  );

  // SCALE
  const scale = new Cartesian3(
    reversed[2].value[0],
    reversed[2].value[1],
    reversed[2].value[2],
  );

  const scaledMatrix = Matrix4.setScale(
    translatedMatrix2,
    scale,
    new Matrix4(),
  );

  // ROTATION
  const x_ord = reversed[3].value[0];
  const x_abs = reversed[3].value[1];

  const rotationMatrix = rotate2(x_ord, x_abs);

  const rotatedMatrix = Matrix4.multiplyByMatrix3(
    scaledMatrix,
    rotationMatrix,
    new Matrix4(),
  );

  // TRANSLATE 3
  const translationCart_3 = new Cartesian3(
    reversed[4].value[0],
    reversed[4].value[1],
    reversed[4].value[2],
  );

  const translatedMatrix3 = Matrix4.multiplyByTranslation(
    rotatedMatrix,
    translationCart_3,
    new Matrix4(),
  );

  return translatedMatrix3;
}

// Rotate in the X-Y plane, using the (x-ordinate, x-abscissa) convention.
// Then convert to cesium matrix 3 rotation matrix
export function rotate2(x_ord: number, x_abs: number) {
  const m = Matrix4.IDENTITY;

  const m00 = m[Matrix4.COLUMN0ROW0]; // 1
  const m01 = m[Matrix4.COLUMN1ROW0]; // 0
  const m02 = m[Matrix4.COLUMN2ROW0]; // 0
  const m03 = m[Matrix4.COLUMN3ROW0]; // 0
  const m10 = m[Matrix4.COLUMN0ROW1]; // 0
  const m11 = m[Matrix4.COLUMN1ROW1]; // 1
  const m12 = m[Matrix4.COLUMN2ROW1]; // 0
  const m13 = m[Matrix4.COLUMN3ROW1]; // 0

  const m_00 = m00 * x_ord - m10 * x_abs; // row 0 column 0
  const m_01 = m01 * x_ord - m11 * x_abs; // row 0 column 1
  const m_02 = m02 * x_ord - m12 * x_abs; // row 0 column 2
  const m_03 = m03 * x_ord - m13 * x_abs; // row 0 column 3
  const m_10 = m00 * x_abs + m10 * x_ord; // row 1 column 0
  const m_11 = m01 * x_abs + m11 * x_ord; // row 1 column 1
  const m_12 = m02 * x_abs + m12 * x_ord; // row 1 column 2
  const m_13 = m03 * x_abs + m13 * x_ord; // row 1 column 3

  const matrix = new Matrix4(
    m_00,
    m_01,
    m_02,
    m_03,
    m_10,
    m_11,
    m_12,
    m_13,
    0,
    0,
    1,
    0,
    0,
    0,
    0,
    1,
  );

  return Matrix4.getRotation(matrix, new Matrix3());
}
